import React, { useState, useMemo } from 'react';
import Modal from 'components/common/Modal';
import { formatarMoeda, formatarData } from '../contasPagar/utils/formatadores';
import { LoadingSpinner } from 'components/common';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Filter, X } from 'lucide-react';

const ModalTransacoesDiarias = ({ isOpen, onClose, transacoes, data, contaNome, loading }) => {
  const [busca, setBusca] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos'); // 'todos', 'receitas', 'despesas'
  const [ordenacao, setOrdenacao] = useState({ campo: 'valor', direcao: 'desc' }); // 'valor', 'descricao', 'id'

  // Filtrar e ordenar transações
  const transacoesFiltradas = useMemo(() => {
    if (!transacoes || transacoes.length === 0) return [];

    let filtradas = [...transacoes];

    // Filtro por tipo (receitas/despesas)
    // Suporta tanto estrutura antiga (valor) quanto nova (entrada/saida)
    if (tipoFiltro === 'receitas') {
      filtradas = filtradas.filter(t => {
        const entrada = parseFloat(t.entrada || 0);
        const valor = parseFloat(t.valor || 0);
        // Considera receita se tem entrada > 0 ou valor > 0 (sem saída)
        return entrada > 0 || (valor > 0 && parseFloat(t.saida || 0) === 0);
      });
    } else if (tipoFiltro === 'despesas') {
      filtradas = filtradas.filter(t => {
        const saida = parseFloat(t.saida || 0);
        const valor = parseFloat(t.valor || 0);
        // Considera despesa se tem saída > 0 ou valor < 0 (sem entrada)
        return saida > 0 || (valor < 0 && parseFloat(t.entrada || 0) === 0);
      });
    }

    // Filtro por busca (descrição)
    if (busca.trim()) {
      const termoBusca = busca.toLowerCase().trim();
      filtradas = filtradas.filter(t => 
        (t.descricao || '').toLowerCase().includes(termoBusca) ||
        String(t.id || '').includes(termoBusca)
      );
    }

    // Ordenação
    filtradas.sort((a, b) => {
      let valorA, valorB;

      switch (ordenacao.campo) {
        case 'valor':
        case 'entrada':
          valorA = parseFloat(a.entrada || a.valor || 0);
          valorB = parseFloat(b.entrada || b.valor || 0);
          break;
        case 'saida':
          valorA = parseFloat(a.saida || 0);
          valorB = parseFloat(b.saida || 0);
          break;
        case 'descricao':
          valorA = (a.descricao || '').toLowerCase();
          valorB = (b.descricao || '').toLowerCase();
          break;
        case 'id':
          valorA = parseInt(a.id || 0);
          valorB = parseInt(b.id || 0);
          break;
        default:
          return 0;
      }

      if (ordenacao.campo === 'descricao') {
        return ordenacao.direcao === 'asc' 
          ? valorA.localeCompare(valorB)
          : valorB.localeCompare(valorA);
      }

      return ordenacao.direcao === 'asc' 
        ? valorA - valorB
        : valorB - valorA;
    });

    return filtradas;
  }, [transacoes, busca, tipoFiltro, ordenacao]);

  const handleOrdenacao = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getOrdenacaoIcon = (campo) => {
    if (ordenacao.campo !== campo) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-[color:var(--text-muted)]" />;
    }
    return ordenacao.direcao === 'asc' 
      ? <ArrowUp className="h-3.5 w-3.5 text-[color:var(--accent)]" />
      : <ArrowDown className="h-3.5 w-3.5 text-[color:var(--accent)]" />;
  };

  const limparFiltros = () => {
    setBusca('');
    setTipoFiltro('todos');
    setOrdenacao({ campo: 'valor', direcao: 'desc' });
  };

  const temFiltrosAtivos = busca.trim() || tipoFiltro !== 'todos';

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Transações do Dia - ${formatarData(data)}`} size="full">
      <div className="space-y-4">
        {/* Informações da conta e data */}
        <div className="text-sm text-[color:var(--text-muted)] mb-4">
          <div className="font-medium text-[color:var(--text-primary)]">Conta: {contaNome}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Data: {formatarData(data)}</div>
        </div>
        
        {loading ? (
          <div className="flex flex-col justify-center items-center py-16">
            <LoadingSpinner inline={true} size="lg" />
            <p className="mt-4 text-[color:var(--text-muted)]">Carregando dados...</p>
          </div>
        ) : !transacoes || transacoes.length === 0 ? (
          <div className="text-center py-8 text-[color:var(--text-muted)]">
            Nenhuma transação encontrada para este dia
          </div>
        ) : (
          <>
            {/* Barra de filtros e busca */}
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-4 border border-[color:var(--border)]">
              <div className="flex flex-col lg:flex-row gap-3">
                {/* Busca */}
                <div className="flex-1">
                  <label htmlFor="modal-transacoes-busca" className="sr-only">Buscar por descrição ou ID</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      id="modal-transacoes-busca"
                      type="text"
                      placeholder="Buscar por descrição ou ID..."
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      className="w-full rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] py-2 pl-10 pr-10 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                    />
                    {busca && (
                      <button
                        type="button"
                        onClick={() => setBusca('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Filtros de tipo */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-[color:var(--text-muted)]" />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTipoFiltro('todos')}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        tipoFiltro === 'todos'
                          ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--avatar-text)] shadow-sm'
                          : 'border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                      } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40`}
                    >
                      Todas
                    </button>
                    <button
                      type="button"
                      onClick={() => setTipoFiltro('receitas')}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        tipoFiltro === 'receitas'
                          ? 'border-[color:var(--status-good)] bg-[color:var(--status-good)] text-[color:var(--avatar-text)] shadow-sm'
                          : 'border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                      } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40`}
                    >
                      Receitas
                    </button>
                    <button
                      type="button"
                      onClick={() => setTipoFiltro('despesas')}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        tipoFiltro === 'despesas'
                          ? 'border-[color:var(--status-bad)] bg-[color:var(--status-bad)] text-[color:var(--avatar-text)] shadow-sm'
                          : 'border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                      } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40`}
                    >
                      Despesas
                    </button>
                  </div>
                </div>

                {/* Botão limpar filtros */}
                {temFiltrosAtivos && (
                  <button
                    type="button"
                    onClick={limparFiltros}
                    className="flex items-center gap-1.5 rounded-lg border border-[color:var(--border)] px-3 py-2 text-xs font-medium text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-muted)] hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                  >
                    <X className="h-3.5 w-3.5" />
                    Limpar
                  </button>
                )}
              </div>

              {/* Contador de resultados */}
              <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
                <div className="flex items-center justify-between text-xs text-[color:var(--text-muted)]">
                  <span>
                    {transacoesFiltradas.length === 0 
                      ? 'Nenhuma transação encontrada'
                      : transacoesFiltradas.length === 1
                      ? '1 transação encontrada'
                      : `${transacoesFiltradas.length} transações encontradas`
                    }
                    {transacoesFiltradas.length !== transacoes.length && (
                      <span className="text-[color:var(--text-muted)] ml-1">
                        (de {transacoes.length} total)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Layout Mobile: Cards */}
            <div className="md:hidden space-y-3">
              {transacoesFiltradas.length === 0 ? (
                <div className="text-center py-8 text-sm text-[color:var(--text-muted)]">
                  Nenhuma transação encontrada com os filtros aplicados
                </div>
              ) : (
                transacoesFiltradas.map((transacao, index) => {
                  const entrada = parseFloat(transacao.entrada || 0);
                  const saida = parseFloat(transacao.saida || 0);
                  const valor = parseFloat(transacao.valor || 0);
                  
                  const saldoLiquido = entrada > 0 || saida > 0 
                    ? entrada - saida 
                    : valor;
                  
                  return (
                    <div key={transacao.id || index} className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-4 shadow-sm">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-mono text-[color:var(--text-muted)] mb-1">
                            #{transacao.id || '-'}
                          </div>
                          <div className="text-sm font-medium text-[color:var(--text-primary)]">
                            {transacao.descricao || '-'}
                          </div>
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)] ml-2 flex-shrink-0">
                          {formatarData(transacao.data) || '-'}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2 pt-3 min-[420px]:grid-cols-3 min-[420px]:gap-3 border-t border-[color:var(--border)]">
                        <div className="flex flex-col min-w-0">
                          <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1">
                            Entrada
                          </div>
                          <div className="text-sm font-semibold text-[color:var(--status-good)] leading-tight">
                            {entrada > 0 ? formatarMoeda(entrada) : '-'}
                          </div>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1">
                            Saída
                          </div>
                          <div className="text-sm font-semibold text-[color:var(--status-bad)] leading-tight">
                            {saida > 0 ? formatarMoeda(saida) : '-'}
                          </div>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1">
                            Saldo
                          </div>
                          <div className={`text-sm font-semibold leading-tight ${
                            saldoLiquido >= 0 ? 'text-[color:var(--status-good)]' : 'text-[color:var(--status-bad)]'
                          }`}>
                            {formatarMoeda(saldoLiquido)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Layout Desktop: Tabela */}
            <div className="hidden md:block border border-[color:var(--border)] rounded-lg overflow-hidden">
              <div className="overflow-y-auto max-h-[65vh]">
                <table className="w-full divide-y divide-[color:var(--border)]">
                  <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => handleOrdenacao('id')}
                          className="flex items-center gap-1.5 transition-colors hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                        >
                          ID
                          {getOrdenacaoIcon('id')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => handleOrdenacao('descricao')}
                          className="flex items-center gap-1.5 transition-colors hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                        >
                          Descrição
                          {getOrdenacaoIcon('descricao')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => handleOrdenacao('entrada')}
                          className="ml-auto flex items-center gap-1.5 transition-colors hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                        >
                          Entrada
                          {getOrdenacaoIcon('entrada')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => handleOrdenacao('saida')}
                          className="ml-auto flex items-center gap-1.5 transition-colors hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                        >
                          Saída
                          {getOrdenacaoIcon('saida')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        Saldo Líquido
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                        Data
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
                    {transacoesFiltradas.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
                          Nenhuma transação encontrada com os filtros aplicados
                        </td>
                      </tr>
                    ) : (
                      transacoesFiltradas.map((transacao, index) => {
                        const entrada = parseFloat(transacao.entrada || 0);
                        const saida = parseFloat(transacao.saida || 0);
                        const valor = parseFloat(transacao.valor || 0);
                        
                        const saldoLiquido = entrada > 0 || saida > 0 
                          ? entrada - saida 
                          : valor;
                        
                        return (
                          <tr key={transacao.id || index} className="hover:bg-[color:var(--surface-muted)]">
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[color:var(--text-muted)] font-mono">
                              #{transacao.id || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-[color:var(--text-primary)]">
                              <div className="max-w-md truncate" title={transacao.descricao || '-'}>
                                {transacao.descricao || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--status-good)] text-right font-medium">
                              {entrada > 0 ? formatarMoeda(entrada) : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--status-bad)] text-right font-medium">
                              {saida > 0 ? formatarMoeda(saida) : '-'}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium text-right ${
                              saldoLiquido >= 0 ? 'text-[color:var(--status-good)]' : 'text-[color:var(--status-bad)]'
                            }`}>
                              {formatarMoeda(saldoLiquido)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--text-muted)]">
                              {formatarData(transacao.data) || '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalTransacoesDiarias;
